import { useEffect, useState } from "react";
import { Lock, KeyRound, ShieldCheck, Gauge } from "lucide-react";
import AdminShell from "../../components/AdminShell";
import api from "../../lib/api";
import { LoadingState, ErrorState } from "../../components/LoadingState";
import { CustomerPanel, MetricCard, StatusBadge, TableWrapper } from "../../components/customer/CustomerUI";

const POSTURE = [
  { key: "encryption_at_rest", label: "Encryption at Rest", icon: Lock },
  { key: "secret_management", label: "Secret Management", icon: KeyRound },
  { key: "api_security", label: "API Security", icon: ShieldCheck },
  { key: "rate_limiting", label: "Rate Limiting", icon: Gauge },
];

export default function SecurityCenter() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => { api.get("/admin/security").then((r) => setData(r.data)).catch(() => setError(true)); }, []);

  const c = data?.counters || {};
  const statusTone = (s) => (["enabled", "healthy", "enforced", "active"].includes(s) ? "success" : "warning");

  if (!data && !error) return <AdminShell title="Security Center" subtitle="Authentication, authorization and platform security posture"><LoadingState cards={4} rows={4} label="Loading Security Center" /></AdminShell>;
  if (error) return <AdminShell title="Security Center" subtitle="Authentication, authorization and platform security posture"><ErrorState message="Security posture data is temporarily unavailable." onRetry={() => window.location.reload()} /></AdminShell>;

  return (
    <AdminShell title="Security Center" subtitle="Authentication, authorization and platform security posture">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {[
          { k: "successful_logins_24h", l: "Logins (24h)", cls: "text-slate-950" },
          { k: "failed_logins_24h", l: "Failed Logins (24h)", cls: "text-amber-400" },
          { k: "authorization_failures", l: "Authz Failures", cls: "text-red-400" },
          { k: "locked_accounts", l: "Locked Accounts", cls: "text-red-400" },
        ].map((x) => (
          <MetricCard key={x.k} data-testid={`security-counter-${x.k}`} label={x.l} value={data ? (c[x.k] ?? 0) : "—"} valueClassName={`text-3xl tracking-tight ${x.cls}`} labelClassName="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500" />
        ))}
      </div>

      <h2 className="mb-3 mt-6 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">Security Posture</h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {POSTURE.map((p) => {
          const item = data?.posture?.[p.key];
          return (
            <CustomerPanel key={p.key}>
              <div className="px-ui-4 py-ui-4 sm:px-ui-5">
                <div className="flex items-center gap-2 text-slate-700"><p.icon className="h-4 w-4" /><span className="text-sm">{p.label}</span></div>
                <StatusBadge className="mt-2" tone={statusTone(item?.status)}>{item?.status || "…"}</StatusBadge>
                <div className="mt-1 text-xs text-slate-500">{item?.detail}</div>
              </div>
            </CustomerPanel>
          );
        })}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <EventTable title="Privileged Activity" events={data?.privileged_activity} testid="privileged" />
        <EventTable title="Recent Security Events" events={data?.recent_security_events} testid="recent-security" />
      </div>
    </AdminShell>
  );
}

function EventTable({ title, events, testid }) {
  return (
    <TableWrapper title={title} data-testid={`event-table-${testid}`}>
      {!events || events.length === 0 ? (
        <div className="p-6 text-center text-sm text-slate-500">No events.</div>
      ) : (
        <table className="w-full text-xs">
          <tbody>
            {events.map((e) => (
              <tr key={e.id} className="border-b border-slate-300 last:border-0">
                <td className="px-3 py-2 font-mono text-slate-500">{(e.ts || "").slice(11, 19)}</td>
                <td className="px-3 py-2 text-slate-700">{e.action}</td>
                <td className="px-3 py-2 text-slate-600">{e.actor_email || "—"}</td>
                <td className="px-3 py-2">
                  <span className={`font-mono ${e.status === "failure" ? "text-red-400" : "text-emerald-400"}`}>{e.status}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </TableWrapper>
  );
}
